// @flow

/**
 * Utilities that deal with date, leveraging client-side Griffin 🎉
 */
import griffinRelativeTime from 'griffin-paypal/relative-time'
import griffinDate from 'griffin-paypal/date'
import getGriffinMetadata, { useGriffinMetadata } from './griffin-utils'
import { RELATIVE_DATES } from '../constants'

/**
 * Get the supported currency pattern for specific style for the current locale
 * @param  {String} style format the date should take - if none provide defaults to m/d/yy
 * @return {String} the formatted date
 */
export function formattedDate(style: string, timeZone: string) {
  const date = new Date()
  const options = { timeZone: timeZone, style }

  return formatDate(date, options)
}

export function formatDate(date: Date, options: Object) {
  const griffinMetadata = getGriffinMetadata()
  const griffinHandler = griffinDate.createHandler(griffinMetadata)

  return griffinHandler.formatDate(date, options)
}

export function formatDateInputMask(style: string) {
  const griffinMetadata = getGriffinMetadata()
  const griffinHandler = griffinDate.createHandler(griffinMetadata)

  const format = {
    EXPIRY: griffinHandler.DATE_CREDIT_CARD_EXP,
    SHORT: griffinHandler.DATE_FORMAT_SHORT,
    FULL: griffinHandler.DATE_FORMAT_FULL,
  }

  const mask = format[style] || format.SHORT
  const dateInputMask = griffinHandler
    .dateInputMask({ style: mask })
    .toUpperCase()

  return dateInputMask
}

/**
 * Hook to obtain GRIFFIN date handler
 */
export function useGriffinDateHandler() {
  const griffinMetadata = useGriffinMetadata()
  const griffinDateHandler = griffinDate.createHandler(griffinMetadata)
  return griffinDateHandler
}

/**
 * Hook to obtain GRIFFIN relative time handler
 */
export function useGriffinRelativeTimeHandler() {
  const griffinMetadata = useGriffinMetadata()
  const griffinRelativeTimeHandler = griffinRelativeTime.createHandler(
    griffinMetadata
  )
  return griffinRelativeTimeHandler
}

/**
 * Check if date is Yesterday based on the user's current location time zone,
 * not on the user's preferred time zone.
 */
export function isYesterday(date: Date) {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  )
}

/**
 * Check if date is Today based on the user's current location time zone,
 * not on the user's preferred time zone.
 */
export function isToday(date: Date) {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

export function getCanonicalRelativeDate(date: Date) {
  if (isYesterday(date)) {
    return RELATIVE_DATES.YESTERDAY
  }
  if (isToday(date)) {
    return RELATIVE_DATES.TODAY
  }
  return null
}
